import { puzzelAuthConfig } from "app/PuzzelAuth/puzzelAuthConfig";
import {
  broadcastPuzzelLogout,
  clearPuzzelTokens,
  getPuzzelIdToken,
  getPuzzelRefreshToken,
  getPuzzelSessionId,
  getValidPuzzelAccessToken,
  registerPuzzelLoginAttempt,
  savePuzzelTokens,
  tryDecodeJwtClaims,
} from "app/PuzzelAuth/puzzelTokenStorage";
import axios from "axios";
import { log, LogLevel } from "logging/datadogBrowserLogs";
import pkceChallenge from "pkce-challenge";

const STATE_KEY = "PUZZEL_AUTH_STATE";
const CODE_VERIFIER_KEY = "PUZZEL_AUTH_CODE_VERIFIER";
const RETURN_PATH_KEY = "PUZZEL_AUTH_RETURN_PATH";

const logSource = "puzzelAuth";

type PuzzelTokenResponse = {
  access_token: string;
  id_token?: string;
  refresh_token?: string;
  expires_in: number;
};

const exchangeTokenRequest = (
  body: URLSearchParams,
): Promise<{ data: PuzzelTokenResponse }> =>
  axios.post(puzzelAuthConfig.tokenEndpoint, body, {
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
  });

// Best-effort - Puzzel may issue opaque tokens instead of JWTs, in which case this is just null.
const logAccessTokenClaims = (accessToken: string): void => {
  log(
    LogLevel.info,
    logSource,
    "Puzzel access token claims (check scope/aud here if API calls 401)",
    tryDecodeJwtClaims(accessToken),
  );
};

export const redirectToPuzzelLogin = async (): Promise<void> => {
  if (!puzzelAuthConfig.clientId) {
    log(
      LogLevel.error,
      logSource,
      "Puzzel client id is not set (expected from /app-data at boot) - cannot start Puzzel login",
    );
    throw new Error("Missing Puzzel client id");
  }

  if (!registerPuzzelLoginAttempt()) {
    log(
      LogLevel.error,
      logSource,
      "Giving up after repeated Puzzel logins still failed - likely a scope/audience or " +
        "customer key misconfiguration rather than an expired session.",
    );
    throw new Error("Too many Puzzel login attempts");
  }

  const { code_verifier: codeVerifier, code_challenge: codeChallenge } =
    await pkceChallenge();
  const state = crypto.randomUUID();

  sessionStorage.setItem(STATE_KEY, state);
  sessionStorage.setItem(CODE_VERIFIER_KEY, codeVerifier);
  sessionStorage.setItem(
    RETURN_PATH_KEY,
    `${window.location.pathname}${window.location.search}`,
  );

  const authorizeParams = new URLSearchParams({
    client_id: puzzelAuthConfig.clientId,
    redirect_uri: puzzelAuthConfig.redirectUri,
    response_type: "code",
    scope: puzzelAuthConfig.scope,
    state,
    code_challenge: codeChallenge,
    code_challenge_method: "S256",
  });
  if (puzzelAuthConfig.idpScheme) {
    authorizeParams.set("idp", puzzelAuthConfig.idpScheme);
  }

  const authorizeUrl = new URL(puzzelAuthConfig.authorizeEndpoint);
  authorizeUrl.search = authorizeParams.toString();

  window.location.assign(authorizeUrl.toString());
};

// Handles the redirect back from Puzzel ID; returns the path to navigate back to after login.
export const handlePuzzelCallback = async (): Promise<string> => {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  const state = params.get("state");
  const expectedState = sessionStorage.getItem(STATE_KEY);
  const codeVerifier = sessionStorage.getItem(CODE_VERIFIER_KEY);
  const returnPath = sessionStorage.getItem(RETURN_PATH_KEY) ?? "/";

  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(CODE_VERIFIER_KEY);
  sessionStorage.removeItem(RETURN_PATH_KEY);

  if (!code || !state || !codeVerifier || state !== expectedState) {
    log(
      LogLevel.error,
      logSource,
      "Puzzel login callback missing/mismatched code, state or code_verifier",
    );
    throw new Error("Invalid Puzzel login callback");
  }

  const response = await exchangeTokenRequest(
    new URLSearchParams({
      grant_type: "authorization_code",
      client_id: puzzelAuthConfig.clientId,
      redirect_uri: puzzelAuthConfig.redirectUri,
      code,
      code_verifier: codeVerifier,
    }),
  );

  savePuzzelTokens({
    accessToken: response.data.access_token,
    idToken: response.data.id_token,
    refreshToken: response.data.refresh_token,
    expiresInSeconds: response.data.expires_in,
  });
  logAccessTokenClaims(response.data.access_token);

  return returnPath;
};

export const refreshPuzzelToken = async (): Promise<string | null> => {
  const refreshToken = getPuzzelRefreshToken();
  if (!refreshToken) {
    return null;
  }

  try {
    const response = await exchangeTokenRequest(
      new URLSearchParams({
        grant_type: "refresh_token",
        client_id: puzzelAuthConfig.clientId,
        refresh_token: refreshToken,
      }),
    );

    savePuzzelTokens({
      accessToken: response.data.access_token,
      idToken: response.data.id_token,
      refreshToken: response.data.refresh_token ?? refreshToken,
      expiresInSeconds: response.data.expires_in,
    });

    return response.data.access_token;
  } catch (error) {
    log(LogLevel.error, logSource, "Failed to refresh Puzzel token", error);
    clearPuzzelTokens();
    return null;
  }
};

// Dedupes concurrent refresh calls so two callers never race on the same (possibly rotating)
// refresh_token - the loser would otherwise wipe out the winner's freshly-saved tokens on failure.
let inFlightRefresh: Promise<string | null> | null = null;

const dedupedRefreshPuzzelToken = (): Promise<string | null> => {
  if (!inFlightRefresh) {
    inFlightRefresh = refreshPuzzelToken().finally(() => {
      inFlightRefresh = null;
    });
  }
  return inFlightRefresh;
};

// Dedupes concurrent callers so an expired token only triggers one refresh-or-redirect attempt.
let inFlightAuth: Promise<string> | null = null;

// Ensures a valid token, refreshing or redirecting to Puzzel login as needed (redirect never resolves).
export const ensurePuzzelAuthenticated = (): Promise<string> => {
  const existingToken = getValidPuzzelAccessToken();
  if (existingToken) {
    return Promise.resolve(existingToken);
  }

  if (!inFlightAuth) {
    inFlightAuth = (async () => {
      try {
        const refreshedToken = await dedupedRefreshPuzzelToken();
        if (refreshedToken) {
          return refreshedToken;
        }

        await redirectToPuzzelLogin();
        return await new Promise<string>(() => {});
      } finally {
        // Skipped if we actually navigated away; only clears the lock when staying on the page.
        inFlightAuth = null;
      }
    })();
  }

  return inFlightAuth;
};

// Non-redirecting check, so Puzzel login can be opt-in instead of blocking the whole dashboard.
export const checkPuzzelSession = async (): Promise<boolean> => {
  if (getValidPuzzelAccessToken()) {
    return true;
  }
  return (await dedupedRefreshPuzzelToken()) !== null;
};

// In-app logout - clears local tokens, broadcasts to other tabs, then ends the Puzzel ID session.
export const logoutFromPuzzel = (): void => {
  const idToken = getPuzzelIdToken();
  const sessionId = getPuzzelSessionId();

  clearPuzzelTokens();
  broadcastPuzzelLogout({ issuer: puzzelAuthConfig.issuer, sessionId });

  const params = new URLSearchParams({
    post_logout_redirect_uri: puzzelAuthConfig.postLogoutRedirectUri,
    // Puzzel needs this to resolve which client's redirect URI whitelist to check - without it,
    // it silently falls back to its own LoggedOut page if id_token_hint can't be validated (e.g. expired).
    client_id: puzzelAuthConfig.clientId,
  });
  if (idToken) {
    params.set("id_token_hint", idToken);
  }
  window.location.assign(
    `${puzzelAuthConfig.endSessionEndpoint}?${params.toString()}`,
  );
};
